import React, { useState } from 'react';
import { domToPng } from 'modern-screenshot';
import { useScreenshotStore, MAX_SCREENSHOTS } from '../stores/screenshotStore';
import { showNotification } from '../services/notificationService';
import { findOpenPopup } from '../utils/popupCapture';
import { useCanCreateTicket } from '../hooks/useCanCreateTicket';

/**
 * Floating "Report Issue" button (bottom-right, circular FAB).
 *
 * Appears once at least one screenshot has been captured (via the header
 * Report-Issue button) and stays reachable above any modal. Its click is
 * context-aware, mirroring the maud_infra_geo behaviour:
 *
 *   - A popup/modal is open (see `findOpenPopup`) → capture ONLY that popup and
 *     add it to the screenshot store (the whole page is NOT captured).
 *   - Normal view (no popup)                     → open the New Ticket /
 *     Report Issue form so the captured shots can be attached and submitted.
 *
 * Flagged with `data-html2canvas-ignore` so it never appears in its own captures.
 */
const ReportIssueButton: React.FC = () => {
  const count = useScreenshotStore((s) => s.screenshots.length);
  const addScreenshot = useScreenshotStore((s) => s.addScreenshot);
  const clearScreenshots = useScreenshotStore((s) => s.clearScreenshots);
  const openTicketModal = useScreenshotStore((s) => s.openTicketModal);
  const ticketModalOpen = useScreenshotStore((s) => s.ticketModalOpen);
  const canCreateTicket = useCanCreateTicket();

  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;

    // Popup/modal open → capture ONLY that popup, not the whole page.
    const popup = findOpenPopup();
    if (popup) {
      // Stop before the (slow) capture if we're already at the 5-shot limit.
      if (count >= MAX_SCREENSHOTS) {
        showNotification('warning', `You can attach up to ${MAX_SCREENSHOTS} screenshots`, 2500);
        return;
      }
      setBusy(true);
      // Immediate feedback on click — the capture below is async.
      showNotification('info', 'Capturing screenshot…', 1200);
      try {
        const dataUrl = await domToPng(popup, {
          scale: window.devicePixelRatio || 1,
          filter: (node: Node) =>
            !(node instanceof HTMLElement && node.getAttribute('data-html2canvas-ignore') === 'true'),
        });
        if (addScreenshot(dataUrl, window.location.href)) {
          showNotification('success', `Popup screenshot captured (${count + 1}/${MAX_SCREENSHOTS})`, 1500);
        }
      } catch (err) {
        console.error('Popup screenshot capture failed:', err);
        showNotification('error', 'Failed to capture popup', 2000);
      } finally {
        setBusy(false);
      }
      return;
    }

    // Normal view (no popup) → open the New Ticket / Report Issue form.
    openTicketModal();
  };

  // Discard all captured screenshots (matches the trash button in maud_infra_geo).
  const handleClear = () => {
    if (busy || count === 0) return;
    clearScreenshots();
    showNotification('info', 'Screenshots cleared', 1500);
  };

  // Nothing to show until a screenshot is captured, and hide while the form is open.
  // Users with no UDA (state/DTCP, or the "All UDAs" view) cannot own a ticket.
  if (!canCreateTicket || count === 0 || ticketModalOpen) return null;

  return (
    <>
      {/* Delete/clear button — sits just above the main FAB. Removes every captured shot. */}
      <button
        type="button"
        data-html2canvas-ignore="true"
        onClick={handleClear}
        disabled={busy}
        title="Delete captured screenshots"
        aria-label="Delete captured screenshots"
        className="fixed bottom-24 right-7 z-[10050] w-10 h-10 rounded-full bg-white text-red-600 border border-red-200 shadow-lg flex items-center justify-center hover:bg-red-50 transition-colors disabled:opacity-60"
      >
        <i className="ri-delete-bin-line text-lg" aria-hidden="true"></i>
      </button>

      <button
        type="button"
        data-html2canvas-ignore="true"
        onClick={handleClick}
        disabled={busy}
        title="Report Issue — capture the open popup, or open a new ticket"
        aria-label="Report Issue"
        className="fixed bottom-6 right-6 z-[10050] w-14 h-14 rounded-full bg-indigo-600 text-white shadow-2xl flex items-center justify-center hover:bg-indigo-700 transition-colors disabled:opacity-60"
      >
        <i className={`ri-customer-service-2-line text-2xl ${busy ? 'animate-pulse' : ''}`} aria-hidden="true"></i>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white pointer-events-none">
            {count}
          </span>
        )}
      </button>
    </>
  );
};

export default ReportIssueButton;
